import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Loader2, Mail, MessageSquare, Phone, Check, MousePointerClick, SendHorizonal, Eye } from 'lucide-react';
import apiV2 from '../../utils/apiV2';
import { toast } from 'sonner';

const CH_ICON = { email: Mail, sms: MessageSquare, whatsapp: Phone };

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};
  return (
    <div className="bg-white rounded-xl shadow-lg ring-1 ring-slate-900/[0.08] px-3.5 py-2.5 text-xs">
      <div className="font-bold text-slate-900 mb-1">{new Date(label).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
      <div className="text-slate-500">Sent <span className="font-extrabold text-[#93C5FD]">{d.sent}</span></div>
      <div className="text-slate-500">Views <span className="font-extrabold text-[#60A5FA]">{d.views}</span></div>
      <div className="text-slate-500">Clicks <span className="font-extrabold text-[#2563EB]">{d.clicks}</span></div>
      {d.bookings > 0 && <div className="text-slate-500">Bookings <span className="font-extrabold text-emerald-600">{d.bookings}</span></div>}
    </div>
  );
};

export const CampaignAnalytics = ({ campaignId, orgId, formatMoney, channels = ['email'] }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testChannel, setTestChannel] = useState(channels[0] || 'email');
  const [testTo, setTestTo] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    let alive = true;
    apiV2.get(`/organizations/${orgId}/marketing/campaigns/${campaignId}/analytics`)
      .then((res) => alive && setData(res.data))
      .catch(() => toast.error('Could not load analytics'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [campaignId, orgId]);

  const sendTest = async () => {
    const isEmail = testChannel === 'email';
    if (isEmail && !testTo.includes('@')) { toast.error('Enter a valid email'); return; }
    if (!isEmail && testTo.replace(/\D/g, '').length < 8) { toast.error('Enter a valid phone number'); return; }
    setSendingTest(true);
    try {
      await apiV2.post(`/organizations/${orgId}/marketing/campaigns/${campaignId}/send-test`, {
        channel: testChannel,
        email: isEmail ? testTo : null,
        phone: isEmail ? null : testTo,
      });
      toast.success(`Test ${isEmail ? 'email' : testChannel === 'sms' ? 'SMS' : 'WhatsApp message'} sent to ${testTo}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>;
  }
  if (!data) return null;

  const k = data.kpis;
  const hasVolume = data.daily.some((d) => d.sent > 0 || d.clicks > 0);

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`campaign-analytics-${campaignId}`}>
      {/* KPI strip */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 mb-4">
        {[
          { label: 'Delivered', value: k.delivered.toLocaleString() },
          { label: 'Views', value: k.views.toLocaleString() },
          { label: 'Clicks', value: k.clicks.toLocaleString() },
          { label: 'CTR', value: `${k.ctr}%` },
          { label: 'Spend', value: `$${(k.costCents / 100).toFixed(2)}` },
          { label: 'Bookings', value: k.attributedBookings.toLocaleString(), hl: k.attributedBookings > 0 },
          { label: 'Revenue', value: k.attributedRevenue > 0 ? formatMoney(k.attributedRevenue) : '—', hl: k.attributedRevenue > 0 },
        ].map((m) => (
          <div key={m.label}>
            <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">{m.label}</div>
            <div className={`text-sm font-extrabold ${m.hl ? 'text-emerald-600' : 'text-slate-800'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Daily performance chart */}
      <div className="rounded-xl bg-slate-50/70 border border-slate-100 p-3 mb-4" data-testid={`campaign-chart-${campaignId}`}>
        <div className="flex items-center gap-4 px-1 mb-1">
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Last 30 days</span>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500"><span className="w-2.5 h-2.5 rounded-sm bg-[#BFDBFE]" /> Sent</span>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500"><span className="w-2.5 h-0.5 rounded bg-[#60A5FA]" /> Views</span>
          <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-slate-500"><span className="w-2.5 h-0.5 rounded bg-[#2563EB]" /> Clicks</span>
        </div>
        {hasVolume ? (
          <ResponsiveContainer width="100%" height={160}>
            <ComposedChart data={data.daily} margin={{ top: 8, right: 4, left: -26, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#E2E8F0" strokeDasharray="3 3" />
              <XAxis
                dataKey="date" tickLine={false} axisLine={false} interval={6}
                tick={{ fontSize: 10, fill: '#94A3B8', fontWeight: 600 }}
                tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}
              />
              <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: '#94A3B8', fontWeight: 600 }} />
              <Tooltip content={<ChartTip />} cursor={{ stroke: '#CBD5E1' }} />
              <Area type="monotone" dataKey="sent" fill="#BFDBFE" stroke="#93C5FD" strokeWidth={1.5} fillOpacity={0.55} />
              <Line type="monotone" dataKey="views" stroke="#60A5FA" strokeWidth={2} strokeDasharray="4 3" dot={false} activeDot={{ r: 3.5 }} />
              <Line type="monotone" dataKey="clicks" stroke="#2563EB" strokeWidth={2.25} dot={false} activeDot={{ r: 4 }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[160px] flex items-center justify-center text-xs text-slate-400 font-semibold">No sends yet — the chart fills in as the drip runs.</div>
        )}
      </div>

      {/* Channel breakdown + send test */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2">
          {data.channels.map((c) => {
            const Icon = CH_ICON[c.channel] || Mail;
            return (
              <span key={c.channel} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 text-[11px] font-bold text-slate-600" data-testid={`campaign-channel-stat-${c.channel}`}>
                <Icon className="w-3.5 h-3.5 text-[#2563EB]" />
                {c.sent} sent · {c.views} views · {c.clicks} clicks · {formatMoney(c.costCents / 100)}
              </span>
            );
          })}
          {data.channels.length === 0 && <span className="text-xs text-slate-400 font-semibold">No channel data yet.</span>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {channels.length > 1 && (
            <select
              value={testChannel}
              onChange={(e) => { setTestChannel(e.target.value); setTestTo(''); }}
              data-testid={`campaign-test-channel-${campaignId}`}
              className="px-2.5 py-2 rounded-full border border-slate-200 text-xs font-bold text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30"
            >
              {channels.map((ch) => (
                <option key={ch} value={ch}>{ch === 'email' ? 'Email' : ch === 'sms' ? 'SMS' : 'WhatsApp'}</option>
              ))}
            </select>
          )}
          <input
            value={testTo} onChange={(e) => setTestTo(e.target.value)}
            type={testChannel === 'email' ? 'email' : 'tel'}
            placeholder={testChannel === 'email' ? 'you@company.com' : '+1 555 123 4567'}
            data-testid={`campaign-test-email-input-${campaignId}`}
            className="w-44 px-3 py-2 rounded-full border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30"
          />
          <button onClick={sendTest} disabled={sendingTest} data-testid={`campaign-send-test-btn-${campaignId}`}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#2563EB] text-white text-xs font-bold hover:bg-[#1D4ED8] disabled:opacity-50">
            {sendingTest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <SendHorizonal className="w-3.5 h-3.5" />}
            Send test
          </button>
        </div>
      </div>

      {/* Activity feed */}
      {data.activity.length > 0 && (
        <div className="overflow-x-auto" data-testid={`campaign-activity-${campaignId}`}>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[9px] font-bold uppercase tracking-wider text-slate-400">
                <th className="pb-2 pr-2">Contact</th>
                <th className="pb-2 px-2">Channel</th>
                <th className="pb-2 px-2">Sent</th>
                <th className="pb-2 px-2">Viewed</th>
                <th className="pb-2 px-2">Clicked</th>
                <th className="pb-2 pl-2">Booked</th>
              </tr>
            </thead>
            <tbody>
              {data.activity.map((a, i) => {
                const Icon = CH_ICON[a.channel] || Mail;
                return (
                  <tr key={i} className="border-t border-slate-50">
                    <td className="py-2 pr-2">
                      <div className="font-semibold text-slate-800 text-[12px]">{a.name}</div>
                      <div className="text-[10px] text-slate-400 font-mono">{a.contact}</div>
                    </td>
                    <td className="py-2 px-2"><Icon className="w-3.5 h-3.5 text-slate-400" /></td>
                    <td className="py-2 px-2 text-[11px] text-slate-500">{a.sentAt ? new Date(a.sentAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—'}</td>
                    <td className="py-2 px-2">
                      {a.viewed
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 text-[10px] font-bold"><Eye className="w-3 h-3" /> Yes</span>
                        : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="py-2 px-2">
                      {a.clicked
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-[#2563EB] text-[10px] font-bold"><MousePointerClick className="w-3 h-3" /> Yes</span>
                        : <span className="text-xs text-slate-300">—</span>}
                    </td>
                    <td className="py-2 pl-2">
                      {a.booked
                        ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold"><Check className="w-3 h-3" /> Yes</span>
                        : <span className="text-xs text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
